import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useSeo } from "../hooks/useSeo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { CONTACT_PLACEHOLDERS } from "../data/content";
import { AlertTriangle, MapPin, Phone, Mail, Clock, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { Indirizzo: MapPin, Telefono: Phone, Email: Mail, "Disponibilità": Clock, Sede: MapPin };

const initialForm = { nome: "", email: "", telefono: "", messaggio: "", consenso: false };

export default function Contatti() {
  useSeo(
    "Contatti — Richiedi informazioni | Psicologa a Macerata",
    "Richiedi informazioni o un primo colloquio con Susanna Paoletti, psicologa e psicoterapeuta a Macerata. Modulo riservato con gestione del consenso privacy."
  );

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const validate = () => {
    const e = {};
    if (form.nome.trim().length < 2) e.nome = "Inserisci il tuo nome.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Inserisci un'email valida.";
    if (form.telefono && !/^[+\d\s().-]{5,}$/.test(form.telefono)) e.telefono = "Numero di telefono non valido.";
    if (form.messaggio.trim().length < 10) e.messaggio = "Scrivi almeno una breve descrizione (min. 10 caratteri).";
    if (!form.consenso) e.consenso = "È necessario fornire il consenso per procedere.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const update = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Controlla i campi evidenziati.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success(data.message || "Richiesta inviata correttamente.");
      setForm(initialForm);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Si è verificato un errore. Riprova più tardi.";
      toast.error(typeof msg === "string" ? msg : "Si è verificato un errore.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (field) =>
    `w-full bg-transparent border-b py-3 text-ink placeholder:text-stone/50 focus:outline-none transition-colors duration-300 ${
      errors[field] ? "border-destructive" : "border-border focus:border-sage"
    }`;

  return (
    <>
      <PageHeader
        eyebrow="Contatti"
        title="Richiedi informazioni"
        subtitle="Descrivi sinteticamente il motivo del contatto, evitando l'invio di informazioni sanitarie particolarmente sensibili. Ti risponderò appena possibile."
      />

      <section className="py-16 md:py-24" data-testid="contatti-content">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Form */}
          <div className="lg:col-span-7">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-sand border border-border rounded-2xl p-10 text-center"
                data-testid="contact-success"
              >
                <CheckCircle2 size={48} className="mx-auto text-sage" />
                <h2 className="font-serif text-3xl text-ink mt-5">Grazie per avermi scritto</h2>
                <p className="text-stone mt-3 max-w-md mx-auto leading-relaxed">
                  La tua richiesta è stata inviata correttamente. Ti risponderò appena possibile.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-6 text-sage-dark underline underline-offset-2 text-sm"
                  data-testid="contact-reset"
                >
                  Invia un'altra richiesta
                </button>
              </motion.div>
            ) : (
              <Reveal>
                <form onSubmit={onSubmit} noValidate className="space-y-8" data-testid="contact-form">
                  <div>
                    <label htmlFor="nome" className="block text-sm text-stone mb-1">Nome *</label>
                    <input
                      id="nome"
                      type="text"
                      value={form.nome}
                      onChange={(e) => update("nome", e.target.value)}
                      className={inputCls("nome")}
                      placeholder="Il tuo nome"
                      data-testid="contact-nome"
                      aria-invalid={!!errors.nome}
                    />
                    {errors.nome && <p className="text-destructive text-sm mt-1" data-testid="error-nome">{errors.nome}</p>}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <label htmlFor="email" className="block text-sm text-stone mb-1">Email *</label>
                      <input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        className={inputCls("email")}
                        placeholder="nome@esempio.it"
                        data-testid="contact-email"
                        aria-invalid={!!errors.email}
                      />
                      {errors.email && <p className="text-destructive text-sm mt-1" data-testid="error-email">{errors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="telefono" className="block text-sm text-stone mb-1">Telefono (facoltativo)</label>
                      <input
                        id="telefono"
                        type="tel"
                        value={form.telefono}
                        onChange={(e) => update("telefono", e.target.value)}
                        className={inputCls("telefono")}
                        placeholder="Es. +39 ..."
                        data-testid="contact-telefono"
                        aria-invalid={!!errors.telefono}
                      />
                      {errors.telefono && <p className="text-destructive text-sm mt-1" data-testid="error-telefono">{errors.telefono}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="messaggio" className="block text-sm text-stone mb-1">Messaggio *</label>
                    <textarea
                      id="messaggio"
                      rows={5}
                      value={form.messaggio}
                      onChange={(e) => update("messaggio", e.target.value)}
                      className={`${inputCls("messaggio")} resize-none`}
                      placeholder="Descrivi brevemente il motivo del contatto…"
                      data-testid="contact-messaggio"
                      aria-invalid={!!errors.messaggio}
                    />
                    {errors.messaggio && <p className="text-destructive text-sm mt-1" data-testid="error-messaggio">{errors.messaggio}</p>}
                  </div>

                  <div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.consenso}
                        onChange={(e) => update("consenso", e.target.checked)}
                        className="mt-1 accent-sage h-4 w-4 shrink-0"
                        data-testid="contact-consenso"
                        aria-invalid={!!errors.consenso}
                      />
                      <span className="text-sm text-stone leading-relaxed">
                        Ho letto l'informativa sulla privacy e acconsento al trattamento dei miei dati personali
                        per rispondere alla mia richiesta. *
                      </span>
                    </label>
                    {errors.consenso && <p className="text-destructive text-sm mt-1" data-testid="error-consenso">{errors.consenso}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-sage text-cream text-sm font-medium tracking-wide transition-[background-color,transform] duration-300 hover:bg-sage-dark hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
                    data-testid="contact-submit"
                  >
                    {loading ? "Invio in corso…" : <>Invia la richiesta <Send size={17} /></>}
                  </button>
                </form>
              </Reveal>
            )}
          </div>

          {/* Aside */}
          <aside className="lg:col-span-5 space-y-6">
            <Reveal delay={0.08}>
              <div className="bg-sand border border-border rounded-2xl p-7">
                <h2 className="font-serif text-2xl text-ink mb-5">Lo studio</h2>
                <ul className="space-y-4">
                  {CONTACT_PLACEHOLDERS.map((c) => {
                    const Icon = ICONS[c.label] || MapPin;
                    return (
                      <li key={c.label} className="flex items-start gap-3" data-testid={`contact-info-${c.label.toLowerCase()}`}>
                        <Icon size={18} className="text-sage-dark mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-stone">{c.label}</p>
                          {c.href ? (
                            <a
                              href={c.href}
                              target={c.href.startsWith("http") ? "_blank" : undefined}
                              rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                              className="text-ink transition-colors duration-300 hover:text-sage-dark break-words"
                            >
                              {c.value}
                            </a>
                          ) : (
                            <p className="text-ink">{c.value}</p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:+393486921696"
                    data-testid="studio-call-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-sage text-cream text-sm font-medium tracking-wide transition-[background-color,transform] duration-300 hover:bg-sage-dark hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-sand"
                  >
                    <Phone size={17} /> Chiama ora
                  </a>
                  <a
                    href="https://www.google.com/maps?q=Via+Emanuele+Filiberto+1,+62100+Macerata+MC"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="studio-map-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 border border-ink/25 text-ink text-sm font-medium tracking-wide transition-[background-color,color,transform,border-color] duration-300 hover:border-ink hover:bg-ink hover:text-cream hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-sand"
                  >
                    <MapPin size={17} /> Apri la mappa
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="overflow-hidden rounded-2xl border border-border" data-testid="contact-map">
                <iframe
                  title="Mappa dello studio — Via Emanuele Filiberto 1, Macerata"
                  src="https://www.google.com/maps?q=Via%20Emanuele%20Filiberto%201,%2062100%20Macerata%20MC&output=embed"
                  className="w-full h-64 md:h-72"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>

            <Reveal delay={0.16}>
              <div className="border border-destructive/40 bg-destructive/5 rounded-2xl p-7" data-testid="emergency-notice">
                <div className="flex items-center gap-3 text-destructive mb-3">
                  <AlertTriangle size={20} />
                  <h3 className="font-serif text-xl">Il modulo non è per le emergenze</h3>
                </div>
                <p className="text-sm text-stone leading-relaxed">
                  Questo modulo non è destinato alle emergenze. In caso di pericolo immediato, contatta il numero
                  unico di emergenza <strong className="text-ink">112</strong> o rivolgiti al pronto soccorso più vicino.
                </p>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
    </>
  );
}
